import { useEffect, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { appCheckReady, functions } from "../../api/firebase";

// 土地の頁のデータ読み込み。データは Firestore にあり、Functions の openTochi からだけ一か所ずつ返る
// （静的ファイルで丸ごと抜かれていたため。nimbalyst-local/plans/tochi-data-protection.md）。
//   req = { muniCd, lv01Nm }（いまの町字）か { v: 村ID }（/tochi?v=）
//   返り値 { data, pages } — data は index.json と同じ形のその場所の分だけ（resolvePlace がそのまま使える）、
//   pages は候補の村の頁 { 村ID: 頁 }
// 同じ場所はこのセッションの間だけ手元に持つ（1日の回数を無駄に使わない）。

export const GSI_REVERSE = "https://mreversegeocoder.gsi.go.jp/reverse-geocoder/LonLatToAddress";

const cache = new Map();

const keyOf = (req) => (req.v ? `v:${req.v}` : `p:${req.muniCd}|${req.lv01Nm || ""}`);

function messageOf(e) {
  if (e?.code === "functions/resource-exhausted") return "今日はこれ以上ひらけません。また明日お越しください";
  return "頁のデータを読み込めなかった";
}

// 開発サーバー（npm start）のときだけ、手元の「本から拾った歴史」と「市の段」（books_build.py が public/chimei/books・city/ に写したもの）を重ねる。
// 本番に流し込む前に手元で見るため。public/chimei/ は hosting に載らない（firebase.json の hosting.ignore）。本番のビルドでは動かない
async function withLocalBooks(res, req) {
  if (process.env.NODE_ENV !== "development" || !res?.pages) return res;
  // 市の段（public/chimei/city/<市区町村コード>.json）
  if (req?.muniCd && res.data && !res.data.city) {
    try {
      const r = await fetch(`/chimei/city/${req.muniCd}.json`);
      if (r.ok && (r.headers.get("content-type") || "").includes("json")) res.data.city = await r.json();
    } catch {
      // 手元に無ければ出さないだけ
    }
  }
  await Promise.all(
    Object.entries(res.pages).map(async ([id, page]) => {
      if (!page || page.books) return;
      try {
        const r = await fetch(`/chimei/books/${encodeURIComponent(id)}.json`);
        if (r.ok && (r.headers.get("content-type") || "").includes("json")) page.books = await r.json();
      } catch {
        // 手元に無ければ出さないだけ
      }
    })
  );
  return res;
}

/** その場所の頁を引く。失敗したら手元から消す（もう一度押せる） */
export function fetchTochi(req) {
  const key = keyOf(req);
  if (!cache.has(key)) {
    const body = req.v ? { v: req.v } : { muniCd: req.muniCd, lv01Nm: req.lv01Nm || "" };
    // App Check の初期化を待ってから（証明が無いと Functions が弾く）
    const p = appCheckReady
      .then(() => httpsCallable(functions, "openTochi")(body))
      .then((r) => withLocalBooks(r.data, req))
      .catch((e) => {
        cache.delete(key);
        throw Object.assign(new Error(messageOf(e)), { code: e?.code });
      });
    cache.set(key, p);
  }
  return cache.get(key);
}

/** req（null なら引かない）の頁。{ data, pages, error } */
export function useTochi(req) {
  const key = req ? keyOf(req) : null;
  const [state, setState] = useState({ key: null, data: null, pages: {}, error: null });
  useEffect(() => {
    if (!req) return undefined;
    let alive = true;
    fetchTochi(req)
      .then((r) => alive && setState({ key, data: r.data, pages: r.pages || {}, error: null }))
      .catch((e) => alive && setState({ key, data: null, pages: {}, error: e.message }));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  // 場所が変わった直後は前の場所の結果を見せない
  return state.key === key ? state : { key, data: null, pages: {}, error: null };
}

/** テスト用: 手元の結果を消す */
export function resetTochiCache() {
  cache.clear();
}
